/**
 * Where a creator lands after uploading a script.
 *
 * Replaces the Artie Score pending page. That page polled every five seconds for a score that is no
 * longer computed on upload, so it would have spun forever — which is worse than showing nothing,
 * because it tells the creator something is coming.
 *
 * DELIBERATELY SAYS NOTHING ABOUT THE INVENTORY. The placement agent runs on this script within a
 * few minutes and finds, prices and categorises every product in it, but none of that is shown here.
 * A count like "we found 76 placements" invites the next question, and about one row in eleven is
 * still in a category a person would reject. The numbers go to the team, who can read them with that
 * caveat; the creator gets an acknowledgement and a real human follow-up.
 *
 * No polling, no timers, nothing to wait on. The page is complete the moment it renders.
 */
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Mail } from "lucide-react";
import logo from "../assets/images/logo-artie.png";

// Callers still pass ?projectId=..., and that is worth keeping in the URL — it makes a support
// conversation traceable to a project. This page deliberately does not READ it: everything it could
// have been used for (a link back to the project, a lookup) is either a redirect loop or a score.
const UploadThanksPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12 text-center relative">
        <button
          onClick={() => navigate("/dashboard")}
          className="absolute top-6 left-6 text-slate-400 hover:text-[#F59E0B] transition-colors flex items-center gap-1.5 text-sm font-medium group cursor-pointer"
        >
          <span className="group-hover:-translate-x-0.5 transition-transform">←</span>
          Dashboard
        </button>

        <div className="w-24 h-24 mx-auto mb-8 relative mt-4">
          <div className="absolute inset-0 bg-emerald-100 rounded-full blur-xl opacity-50"></div>
          <img
            src={logo}
            alt="Artie"
            className="w-full h-full rounded-full object-cover border-4 border-white shadow-lg relative z-10"
          />
          <div className="absolute -right-2 -bottom-2 bg-white p-2 rounded-full shadow-md z-20">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-slate-900 mb-4 font-cabinet">
          Thank you — your script is in
        </h1>

        <p className="text-lg text-slate-600 mb-8 max-w-lg mx-auto font-ibm leading-relaxed">
          Our team is reviewing it now. We'll be in touch directly once we've had a proper look.
        </p>

        <div className="bg-slate-50 rounded-xl p-6 text-left max-w-md mx-auto border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-500" />
            What happens next
          </h3>
          <ul className="space-y-3 text-sm text-slate-600 font-ibm">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
              We read the script and work out where a brand could sit naturally in it.
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
              If it's a fit for something we're working on, we'll email you.
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
              Nothing is shared with a brand without your say-so.
            </li>
          </ul>
        </div>

        {/* No "Back to project" button. It used to navigate to /projects/:projectId, which in
            inventory mode now redirects straight back here — a bounce the creator cannot escape.
            There is also nothing for them to see there: the score is off, and the inventory this
            upload produces is for us, not for them. The dashboard is the honest destination. */}
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button
            onClick={() => navigate("/dashboard")}
            className="px-5 py-2.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadThanksPage;
